'use client';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { fetchArtifactDownloadBlob, shouldFetchArtifactDirectly, } from '#pplx/frontend/apps/ask/artifact-utils/artifactDownload.js';
import { getAssetDownloadURL } from '#pplx/frontend/apps/ask/queries/deeper-research-queries/getAssetDownloadURL.js';
import { getImageDownloadFilename } from '#pplx/frontend/common/files/urls.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
function redactUrlCredentials(url) {
    try {
        const parsed = new URL(url);
        parsed.search = '';
        parsed.hash = '';
        return parsed.toString();
    }
    catch {
        return '[invalid-url]';
    }
}
function getMediaCategory({ assetType, filename, mimeType, }) {
    if (assetType) {
        switch (assetType) {
            case 'GENERATED_VIDEO':
                return 'video';
            case 'AUDIO_FILE':
                return 'audio';
            case 'GENERATED_IMAGE':
            case 'SCREENSHOT':
            case 'CHART':
                return 'image';
        }
    }
    const lowerFilename = filename.toLowerCase();
    if (lowerFilename.endsWith('.mp4') ||
        lowerFilename.endsWith('.mov') ||
        lowerFilename.endsWith('.avi') ||
        mimeType?.startsWith('video/')) {
        return 'video';
    }
    if (lowerFilename.endsWith('.mp3') ||
        lowerFilename.endsWith('.wav') ||
        lowerFilename.endsWith('.ogg') ||
        lowerFilename.endsWith('.flac') ||
        lowerFilename.endsWith('.aac') ||
        lowerFilename.endsWith('.m4a') ||
        mimeType?.startsWith('audio/')) {
        return 'audio';
    }
    return 'image';
}
export const useDownloadFileAsset = ({ reason, }) => {
    const { $t } = useIntl();
    const { openToast } = useToastContext();
    const downloadS3Asset = useCallback(async (request, options) => {
        try {
            // Inline files already contain the original bytes; keep them in the browser.
            const downloadURL = request.url.startsWith('data:')
                ? request.url
                : await getAssetDownloadURL({ request, reason });
            // Create a temporary link element to trigger download
            const link = document.createElement('a');
            link.href = downloadURL;
            link.download = request.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            if (options?.showToast !== false) {
                openToast({
                    message: $t({
                        defaultMessage: 'Downloading file...',
                        id: '5d7ed73ae4',
                        description: 'Toast message shown when file download starts',
                    }),
                    variant: 'success',
                    timeoutSeconds: 3,
                });
            }
        }
        catch (error) {
            logger.error('Failed to download S3 asset', {
                error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
            });
            if (options?.showToast !== false) {
                openToast({
                    message: $t({
                        defaultMessage: 'Failed to download file',
                        id: '6eb8d709cc',
                        description: 'Error toast message when file download fails',
                    }),
                    variant: 'error',
                    timeoutSeconds: 3,
                });
            }
            if (options?.throwOnError) {
                throw error;
            }
        }
    }, [reason, $t, openToast]);
    const downloadMediaAsset = useCallback(async (mediaUrl, filename = 'media', container = document.body, assetType, options) => {
        const resolvedAssetType = assetType ?? 'GENERATED_IMAGE';
        const downloadFilename = resolvedAssetType === 'GENERATED_IMAGE'
            ? getImageDownloadFilename(mediaUrl, filename)
            : filename;
        if (!shouldFetchArtifactDirectly(mediaUrl, resolvedAssetType)) {
            return downloadS3Asset({ url: mediaUrl, filename: downloadFilename }, options);
        }
        try {
            const blob = await fetchArtifactDownloadBlob({
                url: mediaUrl,
                filename: downloadFilename,
                // Callers that omit the type are using this explicitly as a media
                // download, so retain the direct-media fallback behavior.
                assetType: resolvedAssetType,
                reason,
            });
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = downloadFilename;
            container.appendChild(link);
            link.click();
            container.removeChild(link);
            URL.revokeObjectURL(objectUrl);
            const mediaCategory = getMediaCategory({
                assetType,
                filename,
                mimeType: blob.type,
            });
            const successMessage = mediaCategory === 'video'
                ? $t({
                    defaultMessage: 'Video downloaded',
                    id: 'ec6e6b682d',
                    description: 'Success toast message when video download completes',
                })
                : mediaCategory === 'audio'
                    ? $t({
                        defaultMessage: 'Audio downloaded',
                        id: '7adfd81f1f',
                        description: 'Success toast message when audio download completes',
                    })
                    : $t({
                        defaultMessage: 'Image downloaded',
                        id: '63318c74ac',
                        description: 'Success toast message when image download completes',
                    });
            if (options?.showToast !== false) {
                openToast({
                    message: successMessage,
                    variant: 'success',
                    timeoutSeconds: 3,
                });
            }
        }
        catch (error) {
            const mediaCategory = getMediaCategory({ assetType, filename });
            logger.error('Failed to download media asset', {
                error,
                mediaUrl: redactUrlCredentials(mediaUrl),
                errorMessage: error instanceof Error ? error.message : 'Unknown error',
                errorType: error instanceof TypeError && error.message.includes('Failed to fetch')
                    ? 'CORS'
                    : 'Unknown',
            });
            const errorMessage = mediaCategory === 'video'
                ? $t({
                    defaultMessage: 'Failed to download video',
                    id: 'df2fa67fa2',
                    description: 'Error toast message when video download fails',
                })
                : mediaCategory === 'audio'
                    ? $t({
                        defaultMessage: 'Failed to download audio',
                        id: '58ae7afedd',
                        description: 'Error toast message when audio download fails',
                    })
                    : $t({
                        defaultMessage: 'Failed to download image',
                        id: 'b1e26327af',
                        description: 'Error toast message when image download fails',
                    });
            if (options?.showToast !== false) {
                openToast({
                    message: errorMessage,
                    variant: 'error',
                    timeoutSeconds: 3,
                });
            }
            if (options?.throwOnError) {
                throw error;
            }
        }
    }, [$t, openToast, downloadS3Asset, reason]);
    // Keep downloadImageAsset for backward compatibility
    const downloadImageAsset = useCallback(async (imageUrl, filename = 'image.jpg', container = document.body, assetType, options) => {
        return downloadMediaAsset(imageUrl, filename, container, assetType, options);
    }, [downloadMediaAsset]);
    return useMemo(() => ({ downloadS3Asset, downloadImageAsset, downloadMediaAsset }), [downloadImageAsset, downloadS3Asset, downloadMediaAsset]);
};
//# sourceMappingURL=useDownloadS3FileAsset.js.map